import { Trans, i18nMark } from 'lingui-react';
import { last } from 'lodash';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import React, { Component, Fragment } from 'react';
import { compose, setDisplayName } from 'recompose';

import { H2, P, QuickActionButton, Text } from 'base-components';
import { px2rem } from 'decisiv-ui-utils';
import { Row, Column } from 'styled-components-reactive-grid';

import RadioButton from 'blocks/RadioButton';

import DatetimeSelector from 'components/DatetimeSelector';
import Modal from 'components/Modal';

import CaseStatusBadge from 'elements/CaseStatusBadge';
import RadioWrapper from 'elements/RadioWrapper';

import useContext from 'utils/useContext';

import CaseStatusContext from '../CaseStatusContext';
import { CASE_STATUS } from '../constants';

const allowedStatuses = [
  CASE_STATUS.dispatch,
  CASE_STATUS.dispatched,
  CASE_STATUS.enRoute,
  CASE_STATUS.arrived,
  CASE_STATUS.rolling,
];

const labelForStatus = {
  [CASE_STATUS.dispatch]: i18nMark('To Dispatch'),
  [CASE_STATUS.dispatched]: i18nMark('Dispatched'),
  [CASE_STATUS.enRoute]: i18nMark('Dispatched - Enroute'),
  [CASE_STATUS.arrived]: i18nMark('Dispatched - Arrived'),
  [CASE_STATUS.rolling]: i18nMark('Vehicle Rolling'),
};

export class UpdateStatusModal extends Component {
  static propTypes = {
    isUpdatingCaseStatus: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    servicingDealerTimezone: PropTypes.string,
    status: PropTypes.string.isRequired,
    statusHistory: PropTypes.arrayOf(
      PropTypes.shape({ changedAt: PropTypes.string.isRequired }).isRequired,
    ).isRequired,
    updateCaseStatus: PropTypes.func.isRequired,
  };

  static defaultProps = {
    servicingDealerTimezone: undefined,
  };

  constructor(props) {
    super(props);

    const initialStatus =
      allowedStatuses.findIndex(s => s === props.status) + 1;

    this.state = {
      selectedDate: moment().toDate(),
      selectedDateIsValid: true,
      selectedStatus: allowedStatuses[initialStatus],
      selectedTimezone: props.servicingDealerTimezone,
    };
  }

  getEarliestValidTime = () => {
    const { statusHistory } = this.props;

    const { changedAt } = last(statusHistory);
    return moment(changedAt).toDate();
  };

  handleStatusChange = ({ target: { value } }) =>
    this.setState({
      selectedStatus: value,
    });

  updateStatus = () =>
    this.props
      .updateCaseStatus(this.state.selectedStatus, {
        timestamp: this.state.selectedDate.toISOString(),
      })
      .then(this.props.onClose);

  renderRadioButtonForStatus = status => {
    const { status: currentCaseStatus } = this.props;
    const { selectedStatus } = this.state;

    const id = `case_status_${status}`;
    const isCurrentStatus = status === currentCaseStatus;

    return (
      <Row key={status}>
        <Column modifiers={['col', 'padScale_0']}>
          <RadioWrapper modifiers={['compact', isCurrentStatus && 'disabled']}>
            <RadioButton>
              <RadioButton.Label htmlFor={id}>
                <Row modifiers={['middle', 'flex_1']}>
                  <Column style={{ paddingLeft: 0 }} modifiers="padScaleY_0">
                    <CaseStatusBadge status={status} />
                  </Column>
                  <Column modifiers={['col', 'padScaleY_0']}>
                    <Text modifiers="textLight">{labelForStatus[status]}</Text>
                  </Column>
                  <Column style={{ paddingRight: 0 }} modifiers="padScaleY_0">
                    {isCurrentStatus ? (
                      <Text modifiers="small">
                        <Trans>Current Status</Trans>
                      </Text>
                    ) : (
                      <Fragment>
                        <RadioButton.Input
                          id={id}
                          name="status"
                          value={status}
                          checked={status === selectedStatus}
                          onChange={this.handleStatusChange}
                        />
                        {/* This label is what gets styled as the radio button */}
                        <label htmlFor={id} />
                      </Fragment>
                    )}
                  </Column>
                </Row>
              </RadioButton.Label>
            </RadioButton>
          </RadioWrapper>
        </Column>
      </Row>
    );
  };

  render() {
    const { isUpdatingCaseStatus, onClose } = this.props;
    const { selectedStatus } = this.state;

    return (
      <Modal onClose={onClose}>
        {() => (
          <Modal.Body>
            <Modal.Header>
              <Modal.HeaderIcon name="refresh" />
            </Modal.Header>
            <Modal.Content>
              <Row modifiers="center">
                <Column>
                  <H2 modifiers="fontWeightRegular">
                    <Trans>Update Case Status?</Trans>
                  </H2>
                </Column>
              </Row>
              <Row modifiers="center" style={{ marginBottom: px2rem(40) }}>
                <Column modifiers={['col']}>
                  <P>
                    {this.props.servicingDealerTimezone ? (
                      <Trans>
                        Select the new status below, then enter the date and
                        time the status officially changed using the service
                        provider&#39;s time zone.
                      </Trans>
                    ) : (
                      <Trans>
                        Select the new status below, then enter the date and
                        time the status officially changed using your time zone.
                      </Trans>
                    )}
                  </P>
                </Column>
              </Row>
              {allowedStatuses.map(this.renderRadioButtonForStatus)}
              <Row modifiers="middle" style={{ marginTop: px2rem(20) }}>
                <Column modifiers={['col', 'padScale_0']}>
                  <DatetimeSelector
                    onChange={(
                      selectedDate,
                      selectedTimezone,
                      { isValid: selectedDateIsValid },
                    ) =>
                      this.setState({
                        selectedDate,
                        selectedDateIsValid,
                        selectedTimezone,
                      })
                    }
                    selectedDatetime={this.state.selectedDate}
                    selectedTimezone={this.state.selectedTimezone}
                    validBetween={[this.getEarliestValidTime()]}
                  />
                </Column>
              </Row>
            </Modal.Content>
            <Modal.Footer>
              <Row modifiers="end">
                <Column>
                  <QuickActionButton onClick={onClose} type="button">
                    <QuickActionButton.Text>
                      <Trans>Keep Status</Trans>
                    </QuickActionButton.Text>
                  </QuickActionButton>
                </Column>
                <Column>
                  <QuickActionButton
                    type="button"
                    onClick={this.updateStatus}
                    disabled={
                      !selectedStatus ||
                      !this.state.selectedDateIsValid ||
                      isUpdatingCaseStatus
                    }
                    modifiers={[
                      'secondary',
                      (!selectedStatus ||
                        !this.state.selectedDateIsValid ||
                        isUpdatingCaseStatus) &&
                        'disabled',
                    ]}
                  >
                    <QuickActionButton.Text>
                      <Trans>Update Status</Trans>
                    </QuickActionButton.Text>
                  </QuickActionButton>
                </Column>
              </Row>
            </Modal.Footer>
          </Modal.Body>
        )}
      </Modal>
    );
  }
}

export default compose(
  setDisplayName('UpdateStatusModal'),
  useContext(CaseStatusContext),
)(UpdateStatusModal);



// WEBPACK FOOTER //
// ./src/compositions/CaseStatus/CasePanelStatuses/UpdateStatusModal.js